import React from "react";
import { format, differenceInMinutes, addDays } from "date-fns";
import { PhoneIcon, ClockIcon, BanknotesIcon } from "@heroicons/react/24/outline";

export default function AppointmentsSection({
  appointments,
  viewType,
  dateFilter,
  onAppointmentClick,
  onViewTypeChange,
  onDateFilterChange,
  getStatusBadgeClass,
}) {
  const groupAppointmentsByTime = (appointments) => {
    const grouped = {};
    
    // Filter out appointments with invalid dates and cancelled appointments
    const validAppointments = appointments.filter(appointment => {
      try {
        const startDate = new Date(appointment.start);
        const endDate = new Date(appointment.end);
        const isValidDate = !isNaN(startDate) && !isNaN(endDate);
        
        // Check if appointment is cancelled
        const isCancelled = appointment.status && 
          (Array.isArray(appointment.status) 
            ? appointment.status.some(status => status.toLowerCase().includes('cancel'))
            : appointment.status.toLowerCase().includes('cancel')
          );
        
        return isValidDate && !isCancelled;
      } catch (error) {
        return false;
      }
    });
    
    // Group by day for week and month views
    if (dateFilter === "week" || dateFilter === "month") {
      validAppointments.forEach(appointment => {
        try {
          const startDate = new Date(appointment.start);
          const date = format(startDate, 'EEEE, MMMM d');
          const time = format(startDate, 'h:mm a');
          
          if (!grouped[date]) {
            grouped[date] = {};
          }
          if (!grouped[date][time]) {
            grouped[date][time] = [];
          }
          grouped[date][time].push(appointment);
        } catch (error) {
          // Silently skip invalid appointments
        }
      });
    } else {
      // For today/tomorrow, use the current date
      const today = new Date();
      const dateStr = format(dateFilter === "tomorrow" ? addDays(today, 1) : today, 'EEEE, MMMM d');
      grouped[dateStr] = {};
      
      validAppointments.forEach(appointment => {
        try {
          const startDate = new Date(appointment.start);
          // Only include appointments for the selected day
          const appointmentDate = format(startDate, 'EEEE, MMMM d');
          if (appointmentDate === dateStr) {
            const time = format(startDate, 'h:mm a');
            if (!grouped[dateStr][time]) {
              grouped[dateStr][time] = [];
            }
            grouped[dateStr][time].push(appointment);
          }
        } catch (error) {
          // Silently skip invalid appointments
        }
      });
    }
    
    return grouped;
  };

  const renderTimelineView = () => {
    const groupedAppointments = groupAppointmentsByTime(appointments);
    
    return (
      <div className="relative">
        <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200"></div>
        <div className="space-y-8">
          {Object.entries(groupedAppointments)
            .sort(([dateA], [dateB]) => {
              const dateObjA = new Date(dateA.replace('EEEE, ', ''));
              const dateObjB = new Date(dateB.replace('EEEE, ', ''));
              return dateObjA - dateObjB;
            })
            .map(([date, timeGroups]) => (
              <div key={date} className="mb-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 pl-10">{date}</h3>
                <div className="space-y-8">
                  {Object.entries(timeGroups)
                    .sort(([timeA], [timeB]) => {
                      const timeObjA = new Date(`1970/01/01 ${timeA}`);
                      const timeObjB = new Date(`1970/01/01 ${timeB}`);
                      return timeObjA - timeObjB;
                    })
                    .map(([time, dayAppointments]) => (
                      <div key={`${date}-${time}`} className="relative">
                        <div className="absolute left-4 top-4 -ml-2 h-4 w-4 rounded-full bg-blue-600"></div>
                        <div className="pl-10">
                          <div className="text-sm font-medium text-gray-900 mb-2">
                            {time}
                            {dayAppointments.length > 1 && (
                              <span className="ml-2 text-sm text-gray-500">
                                {dayAppointments.length} concurrent appointments
                              </span>
                            )}
                          </div>
                          <div className="space-y-3">
                            {dayAppointments.map((appointment) => {
                              console.log('Rendering appointment:', {
                                id: appointment._id,
                                clientBalance: appointment.clientBalance,
                                hasOutstandingInvoices: appointment.clientBalance?.outstandingInvoices,
                                outstandingBalance: appointment.clientBalance?.outstandingBalance,
                                shouldShowRed: appointment.clientBalance?.outstandingInvoices || appointment.clientBalance?.outstandingBalance > 0
                              });
                              return (
                              <div
                                key={appointment._id}
                                className={`rounded-sm border p-4 hover:shadow-md transition-shadow duration-200 cursor-pointer ${
                                  (appointment.clientBalance?.outstandingInvoices || appointment.clientBalance?.outstandingBalance > 0)
                                    ? 'bg-red-50 border-red-200' 
                                    : 'bg-white border-gray-200'
                                }`}
                                onClick={() => onAppointmentClick(appointment)}
                              >
                                <div className="flex justify-between items-start">
                                  <div>
                                    <h3 className="text-sm font-medium text-gray-900">
                                      {appointment.client?.firstName} {appointment.client?.lastName}
                                    </h3>
                                    <div className="mt-1 flex items-center">
                                      <span className="text-sm text-gray-500">
                                        {appointment.clinician?.firstName} {appointment.clinician?.lastName}
                                      </span>
                                      <span className="mx-2 text-gray-300">·</span>
                                      <span className="text-sm text-gray-500">
                                        {appointment.service?.description}
                                      </span>
                                      <span className="mx-2 text-gray-300">·</span>
                                      <span className="text-sm text-gray-500">
                                        {differenceInMinutes(new Date(appointment.end), new Date(appointment.start))} min
                                      </span>
                                    </div>
                                    <div className="mt-1">
                                      <span className="text-xs text-blue-600 font-medium">
                                        📍 {appointment.clinic?.name || "No clinic"}
                                      </span>
                                    </div>
                                  </div>
                                  <div>
                                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getStatusBadgeClass(appointment.status?.[0] || 'Booked')}`}>
                                      {appointment.status?.[0] || 'Booked'}
                                    </span>
                                  </div>
                                </div>
                                {(appointment.clientBalance?.outstandingInvoices || appointment.clientBalance?.outstandingBalance > 0) && (
                                  <div className="mt-3 flex items-center text-red-700 bg-red-100 rounded px-2 py-1 text-sm font-semibold">
                                    <BanknotesIcon className="h-5 w-5 mr-2" />
                                    Outstanding Balance: ${(appointment.clientBalance?.outstandingBalance || 0).toFixed(2)}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            ))}
        </div>
      </div>
    );
  };

  const renderListView = () => {
    // Filter out cancelled appointments for list view
    const filteredAppointments = appointments.filter(appointment => {
      try {
        const isCancelled = appointment.status && 
          (Array.isArray(appointment.status) 
            ? appointment.status.some(status => status.toLowerCase().includes('cancel'))
            : appointment.status.toLowerCase().includes('cancel')
          );
        return !isCancelled;
      } catch (error) {
        return true; // Include appointment if we can't determine status
      }
    });

    return (
      <div className="space-y-4">
        {filteredAppointments.map((appointment) => (
        <div
          key={appointment._id}
          className={`flex items-center justify-between p-4 border-b hover:bg-gray-50 cursor-pointer ${
            (appointment.clientBalance?.outstandingInvoices || appointment.clientBalance?.outstandingBalance > 0)
              ? 'bg-red-50 border-red-200' 
              : 'border-gray-200'
          }`}
          onClick={() => onAppointmentClick(appointment)}
        >
          <div className="min-w-0 flex-1">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-900">
                  {appointment.client?.firstName} {appointment.client?.lastName}
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  {appointment.service?.description || 'No service description'}
                </p>
                <p className="text-xs text-blue-600 font-medium mt-1">
                  📍 {appointment.clinic?.name || "No clinic"}
                </p>
              </div>
              <div className="ml-2">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeClass(appointment.status?.[0] || 'Booked')}`}>
                  {appointment.status?.[0] || 'Booked'}
                </span>
              </div>
            </div>
            <div className="mt-2 flex items-center justify-between">
              <div className="flex items-center text-sm text-gray-500">
                <ClockIcon className="mr-1.5 h-5 w-5 flex-shrink-0 text-gray-400" />
                <div>
                  <p className="font-medium">
                    {format(new Date(appointment.start), 'EEEE, MMMM d, yyyy')}
                  </p>
                  <p>
                    {format(new Date(appointment.start), 'h:mm a')} -{' '}
                    {format(new Date(appointment.end), 'h:mm a')} (
                    {differenceInMinutes(new Date(appointment.end), new Date(appointment.start))} min)
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                {appointment.status?.includes('Not Confirmed') && (
                  <button
                    type="button"
                    className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    <PhoneIcon className="mr-1.5 h-4 w-4" />
                    Call
                  </button>
                )}
              </div>
            </div>
            {(appointment.clientBalance?.outstandingInvoices || appointment.clientBalance?.outstandingBalance > 0) && (
              <div className="mt-3 flex items-center text-red-700 bg-red-100 rounded px-2 py-1 text-sm font-semibold">
                <BanknotesIcon className="h-5 w-5 mr-2" />
                Outstanding Balance: ${(appointment.clientBalance?.outstandingBalance || 0).toFixed(2)}
              </div>
            )}
          </div>
        </div>
        ))}
      </div>
    );
  };

  return (
    <div className="bg-white rounded-sm shadow">
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-medium text-gray-900">Appointments</h2>
            <div className="mt-2 flex space-x-2">
              <button
                onClick={() => onViewTypeChange("timeline")}
                className={`px-3 py-1 text-sm rounded-sm ${
                  viewType === "timeline" ? "bg-indigo-100 text-indigo-700" : "text-gray-500 hover:bg-gray-100"
                }`}
              >
                Timeline
              </button>
              <button
                onClick={() => onViewTypeChange("list")}
                className={`px-3 py-1 text-sm rounded-sm ${
                  viewType === "list" ? "bg-indigo-100 text-indigo-700" : "text-gray-500 hover:bg-gray-100"
                }`}
              >
                List
              </button>
            </div>
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => onDateFilterChange("day")}
              className={`px-4 py-2 text-sm rounded-sm ${
                dateFilter === "day" ? "bg-indigo-100 text-indigo-700" : "text-gray-500 hover:bg-gray-100"
              }`}
            >
              Day
            </button>
            <button
              onClick={() => onDateFilterChange("week")}
              className={`px-4 py-2 text-sm rounded-sm ${
                dateFilter === "week" ? "bg-indigo-100 text-indigo-700" : "text-gray-500 hover:bg-gray-100"
              }`}
            >
              Week
            </button>
            <button
              onClick={() => onDateFilterChange("month")}
              className={`px-4 py-2 text-sm rounded-sm ${
                dateFilter === "month" ? "bg-indigo-100 text-indigo-700" : "text-gray-500 hover:bg-gray-100"
              }`}
            >
              Month
            </button>
          </div>
        </div>
      </div>
      <div className="p-4">
        {viewType === "timeline" ? renderTimelineView() : renderListView()}
      </div>
    </div>
  );
} 